import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  Clock3,
  Gauge,
  Loader2,
  RefreshCcw,
  Users,
} from "lucide-react";

import {
  getOnPremDashboard,
  getOnPremErrorMessage,
  getOnPremOnboarding,
} from "../../apiOnPremAdmin";
import { firstIncompleteTab } from "./onPremAdminState";
import OnPremUnavailableState from "./OnPremUnavailableState";
import "./OnPremAdmin.css";

const metricDefinitions = [
  { key: "user_count", label: "Configured users", icon: Users },
  { key: "total_tokens_used", label: "Total tokens used", icon: Gauge },
  { key: "failure_count", label: "Failures", icon: AlertCircle },
  { key: "average_response_time_ms", label: "Average response time", icon: Clock3 },
];

export default function OnPremOverviewPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", dashboard: null, onboarding: null, message: "" });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const [dashboardResponse, onboardingResponse] = await Promise.all([
        getOnPremDashboard(),
        getOnPremOnboarding(),
      ]);
      setState({ status: "ready", dashboard: dashboardResponse.data, onboarding: onboardingResponse.data, message: "" });
    } catch (error) {
      const code = error?.response?.status;
      setState({ status: code === 403 ? "denied" : code === 404 ? "unavailable" : "error", dashboard: null, onboarding: null, message: getOnPremErrorMessage(error, "Overview could not be loaded.") });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.status === "loading") return <div className="onprem-loading"><Loader2 className="platform-spin" size={22} /> Loading overview...</div>;
  if (state.status !== "ready") return <div className="onprem-page"><div className="onprem-page-heading"><div><h1>Overview</h1><p>Installation configuration and operational health.</p></div></div>{state.status === "unavailable" ? <OnPremUnavailableState message={state.message} /> : <div className="onprem-state is-error"><AlertCircle size={22} /><div><strong>{state.status === "denied" ? "Access denied" : "Could not load overview"}</strong><p>{state.message}</p></div>{state.status === "error" ? <button type="button" className="onprem-button is-secondary" onClick={load}><RefreshCcw size={15} /> Retry</button> : null}</div>}</div>;

  const onboardingComplete = state.onboarding.onboarding_complete;
  const tab = firstIncompleteTab(state.onboarding);
  return (
    <div className="onprem-page">
      <div className="onprem-page-heading"><div><h1>Overview</h1><p>Installation configuration and operational health.</p></div></div>
      <section className={`onprem-health-card ${onboardingComplete ? "is-ready" : "is-incomplete"}`}>
        <div className="onprem-health-icon"><Activity size={20} /></div>
        <div><h2>{onboardingComplete ? "Installation configured" : "Configuration requires attention"}</h2><p>{onboardingComplete ? "Client, first administrator, and LLM configuration are operational." : "Complete the remaining onboarding configuration before the installation is operational."}</p></div>
        {!onboardingComplete ? <button type="button" className="onprem-button" onClick={() => navigate(`/platform/onboarding?tab=${tab}`)}>{tab === "llm" ? "Configure LLM" : "Continue onboarding"}</button> : null}
      </section>
      <section>
        <div className="onprem-section-head"><div><h2>Observability</h2><p>Operational metrics will appear here when backend collection is available.</p></div><span className="onprem-status is-incomplete">Not yet supported</span></div>
        <div className="onprem-metric-grid">
          {metricDefinitions.map(({ key, label, icon: Icon }) => (
            <article className="onprem-metric" key={key}><Icon size={18} /><span>{label}</span><strong>{state.dashboard.implemented && state.dashboard[key] != null ? state.dashboard[key] : "Not available"}</strong></article>
          ))}
        </div>
      </section>
      <section className="onprem-panel"><div className="onprem-section-head"><div><h2>System summary</h2><p>{state.dashboard.implemented && state.dashboard.summary ? state.dashboard.summary : "A measured system summary is not available yet."}</p></div></div></section>
    </div>
  );
}
